import { Stack, Typography, useTheme } from "@mui/material";
import { Info } from "lucide-react";
import { useEffect, useRef } from "react";
import {
	type Control,
	type FieldPath,
	type FieldValues,
	useFormState,
	useWatch,
} from "react-hook-form";
import { isFilledValue } from "web_myplann/Shared/Utils/isFilledValue";

type Props<TInput extends FieldValues, TOutput> = {
	control: Control<TInput, unknown, TOutput>;
	fields: readonly FieldPath<TInput>[];
	message: string;
};

export const RequiredMinimumMessage = <TInput extends FieldValues, TOutput>({
	control,
	fields,
	message,
}: Props<TInput, TOutput>) => {
	const theme = useTheme();
	const messageRef = useRef<HTMLDivElement>(null);
	const values = useWatch({ control, name: fields });
	const { submitCount } = useFormState({ control });
	const showMessage = submitCount > 0 && !values.some(isFilledValue);

	useEffect(() => {
		if (showMessage) {
			messageRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
		}
	}, [showMessage, submitCount]);

	if (!showMessage) {
		return null;
	}

	return (
		<Stack
			ref={messageRef}
			role="alert"
			direction="row"
			alignItems="flex-start"
			gap={1}
			px={theme.spacingTokens.lg}
			pt={theme.spacingTokens.sm}
		>
			<Info
				size={theme.iconSize.sm}
				color={theme.palette.amber[600]}
				style={{ marginTop: 2, flexShrink: 0 }}
			/>
			<Typography variant="caption" color={theme.palette.amber[700]}>
				{message}
			</Typography>
		</Stack>
	);
};
